import { useAuth } from '@/providers/AuthProvider';
import { useNavigate, useParams } from 'react-router-dom';
import pickAndPlay from '@/assets/images/pickAndPlay.png';
import quickGame from '@/assets/images/quickGame.png';
import { useEffect, useState } from 'react';
import { listGamePackHost } from '@/utils/gamePacks';
import { GamePack } from '@/API';
import { GamePackTile } from '@/components/GamePacks/GamePackTile';
import { useDragToScroll } from '@/hooks/useDragToScroll';
import ReactGA from 'react-ga4';
import FreeTrialBanner from '@/components/FreeTrial/FreeTrialBanner';

function Dashboard() {
  const { tenant, userAttributes } = useAuth();
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [hover, setHover] = useState('a');
  const [gamePacks, setGamePacks] = useState<GamePack[]>([]);
  const scrollRef = useDragToScroll({ direction: 'horizontal', sensitivity: 1 });

  const actualTenantId = tenantId ?? (tenant as string);

  useEffect(() => {
    const fetchData = async () => {
      if (!actualTenantId) return;
      const response = await listGamePackHost();

      setGamePacks(response.data.gamePackList as GamePack[]);
    };

    fetchData();
  }, [actualTenantId]);

  if (!actualTenantId) {
    return null;
  }

  const discoverGamePacks = gamePacks.filter((gamePack) => gamePack.showInDiscover);

  const handleCreateGame = () => {
    ReactGA.event({
      category: 'Homepage',
      action: 'Clicked Button',
      label: 'Create a Game'
    });
    navigate('/dashboard/host');
  };

  const handleCreateQuickGame = () => {
    ReactGA.event({
      category: 'Homepage',
      action: 'Clicked Button',
      label: 'Create a Quick Game'
    });
    navigate('/quick-game');
  };

  return (
    <div className="overflow-auto h-full">
      <div className="w-900 mx-auto content-wrapper">
        <FreeTrialBanner />
        <h1 className="text-h1 text-secondary-navy-500 mb-4">
          Hi {userAttributes?.given_name}! Ready to create a new game?
        </h1>
        <div className="inline-flex gap-16 pt-24">
          <div
            style={{ backgroundImage: `url(${pickAndPlay})` }}
            className="p-16 rounded-xs bg-cover w-442 elevated"
          >
            <h2 className="text-h2 text-secondary-navy-500 mb-5 max-w-2/3">Pick & Play</h2>
            <p className="text-body text-neutral-500 mb-16 max-w-2/3">
              Choose from our curated prompt packs that support your team’s goals.
            </p>
            <button className="btn btn-secondary" onClick={handleCreateGame}>
              Create a Game
            </button>
          </div>
          <div
            style={{ backgroundImage: `url(${quickGame})` }}
            className="p-16 rounded-xs bg-cover w-442 elevated"
          >
            <h2 className="text-h2 text-secondary-navy-500 mb-5 max-w-2/3">Quick Game</h2>
            <p className="text-body text-neutral-500 mb-16 max-w-2/3">
              Short on time? Get a randomly selected prompt in one click.
            </p>
            <button className="btn btn-neutral" onClick={handleCreateQuickGame}>
              Create a Quick Game
            </button>
          </div>
        </div>
      </div>
      <div className="w-full">
        {discoverGamePacks.length > 0 && (
          <>
            <h3 className="w-900 text-h3 mt-44 mb-20 mx-auto">Discover New & Trending Packs</h3>
            <div
              ref={scrollRef}
              className="gamepack-tiles-wrapper overflow-scroll scrollbar-hidden -mt-100 pt-100 pb-100 -mb-100 h-short:pb-60 h-short:-mb-60"
            >
              <div className="inline-flex gap-16">
                {discoverGamePacks.map((gamePack) => (
                  <GamePackTile
                    name={gamePack.name}
                    description={gamePack.description}
                    id={gamePack.id}
                    key={gamePack.id}
                    hover={hover}
                    handleHover={setHover}
                    icon={gamePack.icon}
                    palette={gamePack.palette}
                    promptsCount={gamePack.prompts?.length ?? 0}
                    isNew={gamePack.isNew}
                    categories={[
                      gamePack.category1 ?? '',
                      gamePack.category2 ?? '',
                      gamePack.category3 ?? ''
                    ]}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
